// Раздел «Выбытие»: тонкий слой над /api/disposal и над удалением ошибочных записей.
// Логики здесь нет — только запросы и типы, зеркалящие DTO backend (как service/DeclutterApi.ts).
import createAxiosInstance from '../support/AxiosSupport';

/**
 * Счётчики стадий раздела. Разные числа на разных стадиях законны — это разные состояния
 * одной вещи; важно лишь, чтобы каждое вело к своей выборке.
 */
export interface DisposalOverview {
    candidateCount: number;
    /** Ждут решения в открытой сессии разбора; null — открытой сессии нет. */
    decisionCount: number | null;
    /** Вся очередь открытой сессии; null — сессии нет либо старый бэкенд поля не отдаёт. */
    decisionTotalCount?: number | null;
    pendingCount: number;
    withIntentCount: number;
    retiredCount: number;
    /** Переведены в домашние или рабочие: остались в гардеробе, шкаф не освободился. */
    reassignedCount: number;
}

/** Итоги архива «Выбыли» под текущим срезом: каналы выбытия и источники решения. */
export interface DisposalArchiveSummary {
    total: number;
    byMethod: {code: string; count: number}[];
    bySource: {code: string; count: number}[];
    /** Какие способы вообще встречаются в архиве — варианты фильтра. */
    methods: {code: string; count: number}[];
    /** Какие категории встречаются в архиве — варианты фильтра. */
    categories: {id: string; name: string | null}[];
}

/** Срез архива: все поля необязательны и складываются по «И». */
export interface DisposalArchiveFilter {
    q?: string;
    method?: string[];
    categoryId?: string | null;
    from?: string | null;
    to?: string | null;
}

/** Что исчезнет вместе с ошибочно заведённой записью. */
export interface ItemDeletionImpact {
    itemId: string;
    name: string;
    photoCount: number;
    wearCount: number;
    commentCount: number;
    inTrashBin: boolean;
    retired: boolean;
}

/** Один сработавший триггер кандидата (аудит 17.09, D01). */
export interface DisposalTrigger {
    code: string;
    label: string;
    detail: string | null;
}

/** Сводка очереди по триггерам: из неё строится подпись страницы «Кандидаты». */
export interface DisposalTriggerSummary {
    code: string;
    label: string;
    count: number;
}

/** Уровень безопасности расставания (аудит 17.09, D02). */
export type DisposalSafetyLevel = 'SAFE' | 'SEASON_GAP' | 'LAST_IN_ROLE' | 'UNKNOWN_ROLE';

/** Безопасность одной вещи при уходе всей выбранной группы. */
export interface DisposalItemSafety {
    itemId: string;
    level: DisposalSafetyLevel;
    roleNameRu: string | null;
    remainingInRole: number;
    summary: string;
}

export interface DisposalSafety {
    items: DisposalItemSafety[];
    safeCount: number;
    blockedCount: number;
}

const ownerParams = (ownerId: string | null) => (ownerId ? {ownerId} : {});

/**
 * Пересчёт безопасности выбора (аудит 17.09, D02): безопасность каждой вещи считается при
 * условии, что уйдут все выбранные. Поштучная проверка на этот вопрос не отвечает.
 */
export async function simulateDisposalSafety(ownerId: string | null,
                                             itemIds: string[]): Promise<DisposalSafety> {
    const api = await createAxiosInstance();
    return (await api.post<DisposalSafety>('/disposal/queue/safety', {itemIds},
        {params: ownerParams(ownerId)})).data;
}

/** Что исправляем в ошибочно записанном выбытии (аудит 17.09, D05). */
export interface DisposalCorrection {
    action: 'RETURN' | 'AMEND';
    method?: string | null;
    disposalDate?: string | null;
    note?: string | null;
}

/**
 * Исправление события выбытия: вернуть вещь в гардероб либо переписать способ и дату.
 * Аудит сохраняется — прежние записи журнала помечаются исправленными, а не стираются.
 */
export async function correctDisposalEvent(itemId: string,
                                           correction: DisposalCorrection): Promise<void> {
    const api = await createAxiosInstance();
    await api.post(`/clothing/items/${itemId}/disposal-correction`, correction);
}

export async function fetchDisposalOverview(ownerId: string | null): Promise<DisposalOverview> {
    const api = await createAxiosInstance();
    return (await api.get<DisposalOverview>('/disposal/overview', {params: ownerParams(ownerId)})).data;
}

/**
 * Параметры среза для запроса: пустые поля не отправляем — эндпоинт без них отдаёт весь архив,
 * и лишний `q=` в адресной строке только мешает читать ссылку.
 */
export function archiveFilterParams(filter: DisposalArchiveFilter): Record<string, unknown> {
    const params: Record<string, unknown> = {};
    if (filter.q?.trim()) params.q = filter.q.trim();
    if (filter.method?.length) params.method = filter.method;
    if (filter.categoryId) params.categoryId = filter.categoryId;
    if (filter.from) params.from = filter.from;
    if (filter.to) params.to = filter.to;
    return params;
}

export async function fetchArchiveSummary(
    ownerId: string | null,
    filter: DisposalArchiveFilter,
): Promise<DisposalArchiveSummary> {
    const api = await createAxiosInstance();
    return (await api.get<DisposalArchiveSummary>('/disposal/archive/summary', {
        params: {...ownerParams(ownerId), ...archiveFilterParams(filter)},
    })).data;
}

export async function fetchDeletionImpact(itemId: string): Promise<ItemDeletionImpact> {
    const api = await createAxiosInstance();
    return (await api.get<ItemDeletionImpact>(`/clothing/items/${itemId}/deletion-impact`)).data;
}

/**
 * Удаление ошибочно заведённой записи. Не выбытие: вещи не было, значит не должно остаться
 * ни её истории носки, ни комментариев. Отменить нельзя.
 */
export async function deleteItemRecord(itemId: string): Promise<void> {
    const api = await createAxiosInstance();
    await api.delete(`/clothing/items/${itemId}`);
}
